import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getArchetypeContent, getInteractionPatternsFor } from "@/data/archetypeContent";
import { ArrowLeft } from "lucide-react";

function Bullets({ items }) {
  return (
    <ul className="text-sm space-y-1 list-disc list-inside" style={{ color: "var(--pa-text-primary)" }}>
      {items.map((s, i) => <li key={i}>{s}</li>)}
    </ul>
  );
}

export default function ArchetypeDetail() {
  const { name } = useParams();
  const nav = useNavigate();
  const c = getArchetypeContent(decodeURIComponent(name));
  const patterns = getInteractionPatternsFor(decodeURIComponent(name));

  if (!c) {
    return (
      <div className="pa-card p-6 text-center">
        <p className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>Archetype not found.</p>
        <button className="pa-btn-outline mt-4" onClick={() => nav(-1)}>Back</button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <button className="pa-btn-ghost inline-flex items-center gap-2" onClick={() => nav(-1)}>
        <ArrowLeft size={16} /> Back
      </button>
      <header className="flex items-center gap-4">
        <img src={c.emblem} alt={c.name} className="w-20 h-20 md:w-24 md:h-24 object-contain" />
        <div>
          <h1 className="pa-serif text-3xl md:text-4xl" style={{ color: "var(--pa-text-primary)" }}>{c.name}</h1>
          {c.coreOrientation && (
            <p className="text-sm mt-1" style={{ color: "var(--pa-gold)" }}>{c.coreOrientation}</p>
          )}
        </div>
      </header>

      <blockquote
        className="pa-serif italic text-lg pl-4 border-l-2"
        style={{ borderColor: "var(--pa-gold)", color: "var(--pa-text-secondary)" }}
      >
        {c.coreMessage}
      </blockquote>

      <section className="pa-card p-5">
        <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-gold)" }}>Role</h2>
        <p className="text-sm whitespace-pre-line" style={{ color: "var(--pa-text-primary)" }}>{c.role}</p>
      </section>

      <section className="pa-card p-5">
        <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-gold)" }}>Primary Strengths</h2>
        <Bullets items={c.primaryStrengths} />
      </section>

      <section className="pa-card p-5">
        <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-gold)" }}>Shadow Tendency</h2>
        <p className="text-sm" style={{ color: "var(--pa-text-primary)" }}>{c.shadowTendency}</p>
      </section>

      <section className="pa-card p-5">
        <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-gold)" }}>Best Team Contribution</h2>
        <Bullets items={c.bestTeamContribution} />
      </section>

      <section className="pa-card p-5">
        <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-gold)" }}>Interfaces With The Unknown</h2>
        <p className="text-sm whitespace-pre-line" style={{ color: "var(--pa-text-primary)" }}>{c.interfacesWithUnknown}</p>
      </section>

      <section className="pa-card p-5">
        <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-gold)" }}>Symbol Alignment</h2>
        <Bullets items={c.symbolAlignment} />
      </section>

      <section className="pa-card p-5">
        <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-gold)" }}>Primary Regulation Need</h2>
        <p className="text-sm" style={{ color: "var(--pa-text-primary)" }}>{c.regulationNeed}</p>
        <h3 className="text-sm font-semibold mt-3 mb-1" style={{ color: "var(--pa-gold-light)" }}>Effective grounding includes</h3>
        <Bullets items={c.grounding} />
      </section>

      <section className="pa-card p-5">
        <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-gold)" }}>Working With Them</h2>
        <p className="text-sm" style={{ color: "var(--pa-text-primary)" }}>{c.workingWithThem}</p>
      </section>

      {patterns.length > 0 && (
        <section className="space-y-3">
          <h2 className="pa-serif text-xl" style={{ color: "var(--pa-gold)" }}>Interaction Patterns</h2>
          {patterns.map((p, i) => (
            <div key={i} className="pa-card p-4">
              <p className="pa-serif text-base mb-1" style={{ color: "var(--pa-text-primary)" }}>
                {p.pair.join(" + ")}
              </p>
              <p className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--pa-gold)" }}>{p.friction}</p>
              <Bullets items={p.points} />
              {p.misalignment?.length > 0 && (
                <p className="text-xs mt-2" style={{ color: "var(--pa-text-secondary)" }}>
                  <strong style={{ color: "var(--pa-gold-light)" }}>Misalignment:</strong> {p.misalignment.join("; ")}
                </p>
              )}
              {p.resolution && (
                <p className="text-xs mt-1" style={{ color: "var(--pa-text-secondary)" }}>
                  <strong style={{ color: "var(--pa-gold-light)" }}>Resolution:</strong> {p.resolution}
                </p>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
